import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PatientManagementHeader from "./PatientManagementHeader";
import PatientCard from "./PatientCard";
import VisitModal from "./VisitModal";
import { FaTh, FaList } from "react-icons/fa";

export default function PatientManagement() {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedPatient, setExpandedPatient] = useState(null);
  const [visitPatient, setVisitPatient] = useState(null);
  const [viewMode, setViewMode] = useState("tile"); // "tile" or "list"
  const navigate = useNavigate();
  const [patients, setPatients] = useState([
    {
      id: 1,
      name: "Ramesh Kumar",
      age: 45,
      gender: "Male",
      admissionDate: "2023-11-01",
      ward: "Ward 101",
      doctor: "Dr. Singh",
      status: "Under Treatment",
      notes: "",
      profileImg: null,
      treatments: [
        { id: 1, name: "Physiotherapy", progress: 50 },
        { id: 2, name: "Cardiology Checkup", progress: 30 },
      ],
    },
    // Additional patients can be added here...
  ]);

  // Filter patients based on the search term (if provided)
  const filteredPatients = searchTerm
    ? patients.filter((patient) =>
        patient.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : patients;

  const toggleExpanded = (id) => {
    setExpandedPatient(expandedPatient === id ? null : id);
  };

  const handleVisit = (patient) => {
    setVisitPatient(patient);
  };

  const closeVisit = () => {
    setVisitPatient(null);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto p-6">
        {/* Header */}
        <PatientManagementHeader
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          navigate={navigate}
        />

        {/* View Mode Toggle */}
        <div className="flex justify-end items-center mt-4">
          <button
            className={`p-2 mr-2 rounded ${
              viewMode === "tile"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
            onClick={() => setViewMode("tile")}
          >
            <FaTh />
          </button>
          <button
            className={`p-2 rounded ${
              viewMode === "list"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
            onClick={() => setViewMode("list")}
          >
            <FaList />
          </button>
        </div>

        {/* Patient Cards or Table */}
        <div className="mt-8">
          {viewMode === "tile" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPatients.map((patient) => (
                <PatientCard
                  key={patient.id}
                  patient={patient}
                  expandedPatient={expandedPatient}
                  toggleExpanded={toggleExpanded}
                  handleVisit={handleVisit}
                  setPatients={setPatients}
                  navigate={navigate}
                />
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Age
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gender
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Admission Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ward
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Doctor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPatients.map((patient) => (
                    <tr key={patient.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {patient.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {patient.age}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {patient.gender}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {patient.admissionDate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {patient.ward}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {patient.doctor}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {patient.status}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex space-x-2">
                          <button
                            onClick={() =>
                              navigate(`/new/patient/dashboard/${patient.id}`)
                            }
                            className="text-blue-500 hover:text-blue-700"
                          >
                            Dashboard
                          </button>
                          <button
                            onClick={() => handleVisit(patient)}
                            className="text-yellow-500 hover:text-yellow-700"
                          >
                            Visit
                          </button>
                          <button
                            onClick={() =>
                              navigate(`/new/discharge/${patient.id}`)
                            }
                            className="text-red-500 hover:text-red-700"
                          >
                            Discharge
                          </button>
                          <button
                            onClick={() =>
                              navigate(`/new/bed-allocation/${patient.id}`)
                            }
                            className="text-green-500 hover:text-green-700"
                          >
                            Bed Allocation
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Visit Modal */}
      {visitPatient && (
        <VisitModal patient={visitPatient} closeVisit={closeVisit} />
      )}
    </div>
  );
}
